import { isOffsetDateTime, isUuid } from '@/lib/validation';
import type { ServiceClient } from '../http/client';
import { ApiError } from '../http/errors';

export type ProviderLinkedDataStatus = 'SUCCEEDED' | 'FAILED' | 'UNKNOWN' | 'NOT_CONFIGURED';
export type ProviderLinkedDataFailureReason =
  | 'AUTHENTICATION_UNAVAILABLE'
  | 'AUTHENTICATION_DENIED'
  | 'TIMEOUT'
  | 'CONNECTION_FAILED'
  | 'INVALID_RESPONSE'
  | 'PROJECT_SCOPE_MISMATCH'
  | 'REDIRECT_REJECTED'
  | 'ENVIRONMENT_UNSUPPORTED'
  | 'READ_CAPABILITY_UNAVAILABLE';

export interface ProviderLinkedDataRoom {
  provider_room_id: number;
  room_number: string;
  sales_kind: string;
}

export interface ProviderLinkedData {
  property_id: string;
  environment: 'dev' | 'prod';
  provider: 'EZREMS';
  scope: 'PROJECT_ROOM_READ';
  status: ProviderLinkedDataStatus;
  source: 'LIVE_EZREMS_ROOM_LIST';
  project_mapping_configured: boolean;
  observed_at: string | null;
  failure_reason: ProviderLinkedDataFailureReason | null;
  selected_property: {
    code: string | null;
    label: string | null;
    label_source: 'CORE_PROPERTY_SOURCE_NAME';
  };
  project: { configured_code: string | null; observed_code: string | null };
  limitations: {
    project_name: 'NOT_PROVIDED';
    room_types: 'NOT_PROVIDED';
    core_relations: {
      status: 'UNKNOWN';
      reason: 'AUTHORITATIVE_MAPPING_UNAVAILABLE';
    };
  };
  rooms: ProviderLinkedDataRoom[];
  page: { limit: number; total_count: number | null; next_cursor: string | null };
}

export interface ProviderLinkedDataPageRequest {
  limit?: number;
  cursor?: string;
}

const record = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);
const hasExactKeys = (value: Record<string, unknown>, expected: readonly string[]) =>
  Object.keys(value).length === expected.length &&
  expected.every((key) => Object.prototype.hasOwnProperty.call(value, key));
const dataKeys = [
  'property_id',
  'environment',
  'provider',
  'scope',
  'status',
  'source',
  'project_mapping_configured',
  'observed_at',
  'failure_reason',
  'selected_property',
  'project',
  'limitations',
  'rooms',
  'page',
] as const;
const statuses = new Set<ProviderLinkedDataStatus>([
  'SUCCEEDED',
  'FAILED',
  'UNKNOWN',
  'NOT_CONFIGURED',
]);
const failureReasons = new Set<ProviderLinkedDataFailureReason>([
  'AUTHENTICATION_UNAVAILABLE',
  'AUTHENTICATION_DENIED',
  'TIMEOUT',
  'CONNECTION_FAILED',
  'INVALID_RESPONSE',
  'PROJECT_SCOPE_MISMATCH',
  'REDIRECT_REJECTED',
  'ENVIRONMENT_UNSUPPORTED',
  'READ_CAPABILITY_UNAVAILABLE',
]);
const attemptedFailureReasons = new Set<ProviderLinkedDataFailureReason>([
  'AUTHENTICATION_UNAVAILABLE',
  'AUTHENTICATION_DENIED',
  'TIMEOUT',
  'CONNECTION_FAILED',
  'INVALID_RESPONSE',
  'PROJECT_SCOPE_MISMATCH',
  'REDIRECT_REJECTED',
]);
const invalid = () =>
  new ApiError({
    kind: 'invalid-response',
    service: 'core',
    message: '이지렘스에 등록된 객실 조회 결과 형식이 올바르지 않습니다.',
  });
const nullableString = (value: unknown) => value === null || typeof value === 'string';
const emptyUnattemptedPage = (value: ProviderLinkedData) =>
  value.rooms.length === 0 && value.page.total_count === null && value.page.next_cursor === null;

function isRoom(value: unknown): value is ProviderLinkedDataRoom {
  return (
    record(value) &&
    hasExactKeys(value, ['provider_room_id', 'room_number', 'sales_kind']) &&
    typeof value.provider_room_id === 'number' &&
    Number.isInteger(value.provider_room_id) &&
    value.provider_room_id > 0 &&
    typeof value.room_number === 'string' &&
    value.room_number.length >= 1 &&
    typeof value.sales_kind === 'string' &&
    value.sales_kind.length >= 1
  );
}

function validRooms(value: unknown[]): boolean {
  let previousId = 0;
  for (let index = 0; index < value.length; index += 1) {
    const room = value[index];
    if (!Object.prototype.hasOwnProperty.call(value, index) || !isRoom(room)) return false;
    if (room.provider_room_id <= previousId) return false;
    previousId = room.provider_room_id;
  }
  return true;
}

function validResponseEnvelope(value: unknown): value is { data: unknown } {
  return record(value) && hasExactKeys(value, ['data']);
}

function valid(value: unknown, propertyId: string): value is ProviderLinkedData {
  if (
    !record(value) ||
    !hasExactKeys(value, dataKeys) ||
    !isUuid(value.property_id) ||
    value.property_id.toLowerCase() !== propertyId.toLowerCase() ||
    (value.environment !== 'dev' && value.environment !== 'prod') ||
    value.provider !== 'EZREMS' ||
    value.scope !== 'PROJECT_ROOM_READ' ||
    !statuses.has(value.status as ProviderLinkedDataStatus) ||
    value.source !== 'LIVE_EZREMS_ROOM_LIST' ||
    typeof value.project_mapping_configured !== 'boolean' ||
    !(value.observed_at === null || isOffsetDateTime(value.observed_at)) ||
    !(
      value.failure_reason === null ||
      failureReasons.has(value.failure_reason as ProviderLinkedDataFailureReason)
    ) ||
    !record(value.selected_property) ||
    !hasExactKeys(value.selected_property, ['code', 'label', 'label_source']) ||
    !nullableString(value.selected_property.code) ||
    !nullableString(value.selected_property.label) ||
    value.selected_property.label_source !== 'CORE_PROPERTY_SOURCE_NAME' ||
    !record(value.project) ||
    !hasExactKeys(value.project, ['configured_code', 'observed_code']) ||
    !nullableString(value.project.configured_code) ||
    !nullableString(value.project.observed_code) ||
    value.project_mapping_configured !==
      (typeof value.project.configured_code === 'string' &&
        value.project.configured_code.length > 0) ||
    !record(value.limitations) ||
    !hasExactKeys(value.limitations, ['project_name', 'room_types', 'core_relations']) ||
    value.limitations.project_name !== 'NOT_PROVIDED' ||
    value.limitations.room_types !== 'NOT_PROVIDED' ||
    !record(value.limitations.core_relations) ||
    !hasExactKeys(value.limitations.core_relations, ['status', 'reason']) ||
    value.limitations.core_relations.status !== 'UNKNOWN' ||
    value.limitations.core_relations.reason !== 'AUTHORITATIVE_MAPPING_UNAVAILABLE' ||
    !Array.isArray(value.rooms) ||
    !record(value.page) ||
    !hasExactKeys(value.page, ['limit', 'total_count', 'next_cursor']) ||
    typeof value.page.limit !== 'number' ||
    !Number.isInteger(value.page.limit) ||
    value.page.limit < 1 ||
    value.page.limit > 50 ||
    !(
      value.page.total_count === null ||
      (typeof value.page.total_count === 'number' &&
        Number.isInteger(value.page.total_count) &&
        value.page.total_count >= 1)
    ) ||
    !(value.page.next_cursor === null || typeof value.page.next_cursor === 'string') ||
    (typeof value.page.next_cursor === 'string' &&
      (value.page.next_cursor.length < 1 ||
        value.page.next_cursor.length > 1024 ||
        !/^[A-Za-z0-9_-]+$/.test(value.page.next_cursor))) ||
    value.rooms.length > value.page.limit ||
    value.rooms.length > 50 ||
    (value.page.total_count !== null && value.page.total_count < value.rooms.length) ||
    !validRooms(value.rooms)
  )
    return false;

  const data = value as unknown as ProviderLinkedData;
  switch (data.status) {
    case 'SUCCEEDED':
      return (
        data.project_mapping_configured &&
        data.observed_at !== null &&
        data.failure_reason === null &&
        data.page.total_count !== null &&
        data.project.observed_code !== null
      );
    case 'FAILED':
      return (
        data.project_mapping_configured &&
        data.observed_at !== null &&
        data.failure_reason !== null &&
        attemptedFailureReasons.has(data.failure_reason) &&
        data.project.observed_code === null &&
        emptyUnattemptedPage(data)
      );
    case 'UNKNOWN':
      return (
        data.project_mapping_configured &&
        data.observed_at === null &&
        (data.failure_reason === 'ENVIRONMENT_UNSUPPORTED' ||
          data.failure_reason === 'READ_CAPABILITY_UNAVAILABLE') &&
        data.project.observed_code === null &&
        emptyUnattemptedPage(data)
      );
    case 'NOT_CONFIGURED':
      return (
        !data.project_mapping_configured &&
        data.observed_at === null &&
        data.failure_reason === null &&
        data.project.observed_code === null &&
        emptyUnattemptedPage(data)
      );
    default:
      return false;
  }
}

function validPageRequest(page: ProviderLinkedDataPageRequest): boolean {
  const limit = page.limit ?? 50;
  return (
    Number.isInteger(limit) &&
    limit >= 1 &&
    limit <= 50 &&
    (page.cursor === undefined ||
      (page.cursor.length >= 1 &&
        page.cursor.length <= 1024 &&
        /^[A-Za-z0-9_-]+$/.test(page.cursor)))
  );
}

export async function readProviderLinkedData(
  client: ServiceClient,
  propertyId: string,
  page: ProviderLinkedDataPageRequest = {},
  signal?: AbortSignal,
): Promise<ProviderLinkedData> {
  if (!isUuid(propertyId) || !validPageRequest(page)) throw invalid();
  const params: Record<string, string | number> = { limit: page.limit ?? 50 };
  if (page.cursor !== undefined) params.cursor = page.cursor;
  const response = await client.get<unknown>(
    `admin/properties/${propertyId}/provider-linked-data`,
    {
      params,
      signal,
      timeout: 35_000,
      headers: { 'Cache-Control': 'no-store' },
      preserveEnvelope: true,
    },
  );
  if (!validResponseEnvelope(response) || !valid(response.data, propertyId)) throw invalid();
  if (response.data.page.limit !== (page.limit ?? 50)) throw invalid();
  return response.data;
}
